import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, concatMap, from, last, of, tap } from 'rxjs';
import { AvisosService } from './avisos.service';

/**
 * Un cambio cargado en pantalla que todavía no se guardó.
 *
 * `aplicar` es la llamada real a la API, ya armada por la pestaña que la
 * conoce. El servicio no sabe de provisiones ni de certificaciones: solo las
 * junta, las cuenta y las corre en orden.
 */
export interface CambioPendiente {
  id: string;
  /** De qué pestaña vino, para poder recargar solo lo que hace falta */
  tabla: string;
  /** Qué se hizo, en palabras. Es lo que ve la persona antes de guardar. */
  descripcion: string;
  aplicar: () => Observable<unknown>;
}

/**
 * Lo que se cargó en el proyecto y todavía no se guardó.
 *
 * **Nada persiste ni viaja a ARMUS hasta que se aprieta Guardar.** Antes cada
 * alta escribía en la base y encolaba su movimiento en el acto, así que una
 * provisión cargada por error ya había salido: la única salida era borrarla,
 * y eso también viajaba.
 *
 * El Figma lo pedía así desde el principio: *«3 cambios importantes sin
 * guardar. Se actualizará información vinculada a otros sistemas.»*
 *
 * Las llamadas se corren **en orden y de a una**: si un alta y su corrección
 * salieran en paralelo, ARMUS podría quedarse con el valor viejo.
 */
@Injectable({ providedIn: 'root' })
export class CambiosPendientesService {
  private secuencia = 0;

  private avisos = inject(AvisosService);

  readonly cambios = signal<CambioPendiente[]>([]);

  /**
   * De qué pantalla son los cambios que hay ahora.
   *
   * **El servicio es uno solo y las pantallas son muchas.** Sin esto, los
   * roles a medio cambiar en Usuarios seguían apareciendo al entrar a un
   * proyecto: la barra ofrecía guardar, desde una pantalla que no sabe qué
   * es lo que se está guardando, algo que se cargó en otra. Cada pantalla
   * declara su ámbito al entrar y lo de las demás se descarta —nunca se
   * guardó, así que no hay nada que perder que no se hubiera perdido al
   * recargar—.
   */
  private ambito = signal<string | null>(null);
  readonly guardando = signal(false);
  readonly error = signal<string | null>(null);

  /** Sube cada vez que se guarda o se descarta: las pestañas se recargan con esto */
  readonly refrescos = signal(0);

  readonly cantidad = computed(() => this.cambios().length);
  readonly hay = computed(() => this.cambios().length > 0);

  /** El detalle que se muestra al desplegar la barra */
  readonly descripciones = computed(() => this.cambios().map((c) => c.descripcion));

  registrar(tabla: string, descripcion: string, aplicar: () => Observable<unknown>): string {
    const id = `c${++this.secuencia}`;
    this.cambios.update((cs) => [...cs, { id, tabla, descripcion, aplicar }]);
    return id;
  }

  /**
   * Registra un cambio **reemplazando** el anterior de la misma tabla.
   *
   * Es lo que corresponde cuando lo que se guarda es una lista entera y no una
   * fila: editar un nombre letra por letra registraría un cambio por tecla, la
   * barra diría «14 cambios sin guardar» por una sola edición, y al guardar se
   * mandaría catorce veces la misma lista.
   */
  registrarUnico(tabla: string, descripcion: string, aplicar: () => Observable<unknown>): string {
    const id = `c${++this.secuencia}`;
    this.cambios.update((cs) => [...cs.filter((c) => c.tabla !== tabla), { id, tabla, descripcion, aplicar }]);
    return id;
  }

  /**
   * Saca lo pendiente de una tabla, sin guardarlo.
   *
   * Es para cuando algo **vuelve solo a como estaba**: se tocó un campo y se
   * volvió a escribir el valor original, o se abrió una fila para editarla y
   * se cerró sin tocar nada. Sin esto, la barra dice «1 cambio sin guardar»
   * sobre un cambio que no existe, y guardar manda una escritura que no
   * cambia nada —a ARMUS incluido.
   */
  olvidar(tabla: string): void {
    this.cambios.update((cs) => cs.filter((c) => c.tabla !== tabla));
  }

  /** Saca un cambio sin guardarlo. Sirve para deshacer uno solo. */
  quitar(id: string): void {
    this.cambios.update((cs) => cs.filter((c) => c.id !== id));
  }

  /**
   * Corre todo, en orden.
   *
   * Si uno falla se para ahí: seguir dejaría la mitad aplicada y la otra mitad
   * no, sin forma de saber cuál era cuál. Los que ya salieron se sacan de la
   * lista; el que falló y los que venían detrás quedan.
   */
  guardar(): Observable<unknown> {
    const pendientes = this.cambios();

    if (pendientes.length === 0 || this.guardando()) return of(null);

    this.guardando.set(true);
    this.error.set(null);

    /* Cuáles de los que se guardaron **no llegaron a ARMUS**. Se junta acá y
       se avisa una sola vez al final: un toast por cambio sería una fila de
       carteles diciendo lo mismo. */
    let enCola = 0;

    return from(pendientes).pipe(
      concatMap((c) =>
        c.aplicar().pipe(
          tap((r) => {
            if (this.quedoEnCola(r)) enCola++;

            this.cambios.update((cs) => cs.filter((x) => x.id !== c.id));
          })
        )
      ),
      last(null, null),
      tap({
        next: () => {
          this.guardando.set(false);
          this.refrescos.update((n) => n + 1);

          if (enCola > 0) this.avisarCola(enCola);
        },
        error: (e) => {
          const mensaje = (e as { error?: { message?: string } })?.error?.message;
          this.error.set(mensaje ?? 'No se pudieron guardar los cambios.');
          this.guardando.set(false);

          /* **No se refresca al fallar.** Refrescar recarga cada pestaña desde
             el servidor y se lleva puesto lo que se había editado y todavía no
             salió —justo lo que hay que corregir para reintentar—. Pasó con el
             EDT: al rechazarse por falta de motivo, el árbol volvía a lo
             guardado y el cambio a justificar desaparecía.

             Queda algo desactualizado lo que sí se aplicó, y es el mal menor:
             se acomoda en el próximo guardado. */
        }
      })
    );
  }

  /**
   * Si el cambio se guardó acá pero **no llegó a ARMUS**.
   *
   * Las respuestas traen el resultado del envío de dos formas según de dónde
   * vengan: `armus: 'error'` o `armus: { estado }`. Se leen las dos porque lo
   * que importa es lo mismo, y unificar el contrato es una limpieza aparte.
   */
  private quedoEnCola(respuesta: unknown): boolean {
    const armus = (respuesta as { armus?: unknown })?.armus;

    if (!armus) return false;

    const estado = typeof armus === 'string'
      ? armus
      : (armus as { estado?: string })?.estado;

    /* Sólo «enviado» es haber llegado. Pendiente es circuito apagado —a
       propósito— y error es que no se pudo: las dos quedan esperando, y quien
       guardó tiene que saberlo. */
    return !!estado && estado !== 'enviado';
  }

  /**
   * Lo dice al guardar, que es cuando la persona todavía está mirando.
   *
   * Enterarse tres días después, entrando al monitor, es enterarse tarde. Y no
   * es una amenaza: lo encolado sale solo cuando ARMUS vuelve a responder.
   */
  private avisarCola(cuantos: number): void {
    this.avisos.info(
      cuantos === 1 ? 'Un cambio quedó esperando a ARMUS' : `${cuantos} cambios quedaron esperando a ARMUS`,
      'Se guardaron acá. Salen solos en cuanto ARMUS vuelva a responder; también podés '
        + 'forzarlo desde Monitor › Circuitos hacia ARMUS.'
    );
  }

  /** Tira todo lo cargado y vuelve a lo guardado */
  descartar(): void {
    this.cambios.set([]);
    this.error.set(null);
    this.refrescos.update((n) => n + 1);
  }

  /**
   * Declara de quién son los cambios de acá en adelante.
   *
   * Si el ámbito cambia, lo que había se descarta: era de otra pantalla.
   * Idempotente, así que se puede llamar en cada `ngOnInit` sin pensar.
   */
  entrarA(ambito: string): void {
    if (this.ambito() === ambito) return;

    this.ambito.set(ambito);
    this.limpiar();
  }

  /** Al entrar o salir de un proyecto: lo pendiente es de ese proyecto */
  limpiar(): void {
    this.cambios.set([]);
    this.error.set(null);
    this.guardando.set(false);
  }
}
